import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Drawer } from '../../components/Drawer';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { useAddons, usePackages, addAddon, updateAddon } from '../../api/catalog';

type AddonRow = {
  id: string;
  name: string;
  description: string;
  billing: 'recurring' | 'one_time';
  amount: number;
  status: string;
  inUseCount: number;
  compatiblePackageIds: string[];
  internalCode?: string;
  unitLabel?: string;
};

/** Add-ons — table + create/edit drawer (persists via /admin/add-ons). */
export function Addons() {
  const { data: addons = [], isLoading } = useAddons();
  const { data: packages = [] } = usePackages();
  const queryClient = useQueryClient();
  const [drawer, setDrawer] = useState<{ kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; addon: AddonRow }>({ kind: 'closed' });

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Pricing"
        title="Add-ons"
        subtitle="À-la-carte extras subscribers can buy on top of any package."
        action={<Button iconLeft={<Plus className="w-4 h-4" />} onClick={() => setDrawer({ kind: 'create' })}>New add-on</Button>}
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-700">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3 min-w-[280px]">Add-on</th>
                <th className="text-left font-medium px-4 py-3">Unit</th>
                <th className="text-right font-medium px-4 py-3">Price</th>
                <th className="text-left font-medium px-4 py-3">Billing</th>
                <th className="text-left font-medium px-4 py-3 hidden lg:table-cell min-w-[260px]">Compatible tiers</th>
                <th className="text-right font-medium px-4 py-3 hidden md:table-cell">In use</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3">
                      <Skeleton className="h-4 w-40 max-w-full rounded mb-1.5" />
                      <Skeleton className="h-3 w-24 max-w-full rounded mb-1.5" />
                      <Skeleton className="h-3 w-56 max-w-full rounded" />
                    </td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-20 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded ml-auto" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded" /></td>
                    <td className="px-4 py-3 hidden lg:table-cell"><Skeleton className="h-4 w-32 rounded" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Skeleton className="h-4 w-8 rounded ml-auto" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-5 sm:px-6 py-3"></td>
                  </tr>
                ))
              ) : addons.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No add-ons yet. Create one to offer à-la-carte extras.
                  </td>
                </tr>
              ) : (
                addons.map((a) => {
                const compatibleNames = packages
                  .filter((p) => a.compatiblePackageIds.includes(p.id))
                  .map((p) => p.name);
                const isAllTiers = compatibleNames.length === packages.length;
                return (
                  <tr key={a.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                    <td className="px-5 sm:px-6 py-3">
                      <div className="font-medium text-zinc-950 dark:text-zinc-50">{a.name}</div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 mono">{a.internalCode}</div>
                      <div className="mt-1 text-xs text-zinc-600 dark:text-zinc-300 max-w-md leading-snug">
                        {a.description}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 mono text-xs whitespace-nowrap">{a.unitLabel}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Money minor={a.amount} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50 font-medium" />
                    </td>
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 whitespace-nowrap">
                      {a.billing === 'recurring' ? 'Recurring' : 'One-time'}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {isAllTiers ? (
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">All tiers</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {compatibleNames.map((name) => (
                            <span
                              key={name}
                              className="px-1.5 py-px rounded text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200 hidden md:table-cell">{a.inUseCount}</td>
                    <td className="px-4 py-3">
                      <Badge tone={a.status === 'active' ? 'success' : 'neutral'}>
                        {a.status === 'active' ? 'Active' : 'Draft'}
                      </Badge>
                    </td>
                    <td className="px-5 sm:px-6 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setDrawer({ kind: 'edit', addon: a as unknown as AddonRow })}
                        className="px-2.5 py-1 rounded text-xs font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
        Add-ons are charged on top of the base subscription. Capped-tier add-ons (extra products /
        invoices) only apply to Starter / Basic / Regular — Standard and above already include
        unlimited products and invoices.
      </p>

      {drawer.kind !== 'closed' && (
        <AddonDrawer
          mode={drawer}
          onClose={() => setDrawer({ kind: 'closed' })}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['addons'] });
            setDrawer({ kind: 'closed' });
          }}
        />
      )}
    </div>
  );
}

function AddonDrawer({
  mode,
  onClose,
  onSaved,
}: {
  mode: { kind: 'create' } | { kind: 'edit'; addon: AddonRow };
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = mode.kind === 'edit';
  const seed = editing ? mode.addon : null;
  const { data: packages = [] } = usePackages();

  const [name, setName] = useState(seed?.name ?? '');
  const [description, setDescription] = useState(seed?.description ?? '');
  const [billing, setBilling] = useState<'recurring' | 'one_time'>(seed?.billing ?? 'recurring');
  // Stored MINOR units; the input is BDT → seed ÷100, save ×100.
  const [price, setPrice] = useState(seed ? String(seed.amount / 100) : '');
  const [status, setStatus] = useState<'active' | 'draft'>(seed?.status === 'draft' ? 'draft' : 'active');
  const [tierSelection, setTierSelection] = useState<Set<string>>(
    () => new Set(seed?.compatiblePackageIds ?? []),
  );
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  function toggleTier(id: string) {
    const next = new Set(tierSelection);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setTierSelection(next);
  }

  const priceNum = Number(price);
  const valid = name.trim().length >= 2 && Number.isFinite(priceNum) && priceNum >= 0;

  async function handleSave() {
    if (!valid || submitting) return;
    setSubmitting(true);
    setSaveError(null);
    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      billing,
      priceMinor: Math.round(priceNum * 100),
      status,
      packageIds: [...tierSelection],
    };
    try {
      if (editing) await updateAddon(seed!.id, payload);
      else await addAddon(payload);
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save the add-on. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={editing ? `Edit add-on · ${seed!.name}` : 'New add-on'}
      width="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSave} loading={submitting} disabled={!valid}>
            {editing ? 'Save' : 'Create add-on'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Input label="Name" required placeholder="Extra location" value={name} onChange={(e) => setName(e.target.value)} />
        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Description</label>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Each location adds 1 to your max."
            className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 placeholder-zinc-400 dark:placeholder-zinc-500 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand resize-none"
          />
        </div>
        <Input
          label="Price (BDT)"
          required
          prefix={<span className="text-sm">৳</span>}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          hint={billing === 'recurring' ? 'Charged per billing period on top of the subscription.' : 'Charged once at purchase.'}
        />

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Billing</span>
          <div className="space-y-2">
            <Radio name="billing" checked={billing === 'recurring'} onChange={() => setBilling('recurring')} label="Recurring with subscription" />
            <Radio name="billing" checked={billing === 'one_time'} onChange={() => setBilling('one_time')} label="One-time charge at purchase" />
          </div>
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            Compatible packages
            <span className="ml-1.5 text-xs font-normal text-zinc-500 dark:text-zinc-400">
              ({tierSelection.size} of {packages.length} selected — none selected = shown on all)
            </span>
          </span>
          <div className="grid grid-cols-2 gap-2">
            {packages.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={tierSelection.has(p.id)}
                  onChange={() => toggleTier(p.id)}
                  className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                />
                {p.name}
              </label>
            ))}
          </div>
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Status</span>
          <div className="space-y-2">
            <Radio name="status" checked={status === 'active'} onChange={() => setStatus('active')} label="Active" />
            <Radio name="status" checked={status === 'draft'} onChange={() => setStatus('draft')} label="Draft" />
          </div>
        </div>

        {saveError && (
          <Banner tone="error" title="Couldn’t save the add-on">
            {saveError}
          </Banner>
        )}
      </div>
    </Drawer>
  );
}

function Radio({
  name,
  checked,
  onChange,
  label,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
      <input type="radio" name={name} checked={checked} onChange={onChange} className="text-brand focus:ring-brand" />
      {label}
    </label>
  );
}
